import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TagCriteriaBuilder } from "./TagCriteriaBuilder";
import { IconSelector } from "./IconSelector";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { mockCustomers } from "@/utils/customerGroups";
import { X, User, Filter, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const tagSchema = z.object({
  name: z.string().min(1, "Name is required").max(50, "Name must be less than 50 characters"),
  description: z.string().max(200, "Description must be less than 200 characters").optional(),
  type: z.enum(["static", "dynamic"]),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Invalid color format"),
  icon: z.string().optional(),
  customers: z.array(z.string()).min(1, "At least one customer must be selected"),
});

type TagFormData = z.infer<typeof tagSchema>;

interface CreateTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTagCreated?: () => void;
}

export function CreateTagDialog({ open, onOpenChange, onTagCreated }: CreateTagDialogProps) {
  const [criteria, setCriteria] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: {
      name: "",
      description: "",
      type: "static",
      color: "#3B82F6",
      icon: "Tag",
      customers: [],
    },
  });

  const watchedType = form.watch("type");

  const handleClose = () => {
    form.reset({
      name: "",
      description: "",
      type: "static",
      color: "#3B82F6",
      icon: "Tag",
      customers: [],
    });
    setCriteria(null);
    onOpenChange(false);
  };

  const addCustomer = (customerId: string) => {
    const currentCustomers = form.getValues("customers");
    if (!currentCustomers.includes(customerId)) {
      form.setValue("customers", [...currentCustomers, customerId]);
    }
  };

  const removeCustomer = (customerId: string) => {
    const currentCustomers = form.getValues("customers");
    form.setValue("customers", currentCustomers.filter(id => id !== customerId));
  };

  const onSubmit = async (data: TagFormData) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const tagData = {
        name: data.name,
        description: data.description,
        type: data.type,
        color: data.color,
        icon: data.icon || null,
        created_by: user.id,
        criteria: data.type === "dynamic" ? criteria : null,
        customers: data.customers,
      };

      const { error } = await supabase
        .from("tags")
        .insert([tagData]);

      if (error) throw error;

      toast({
        title: "Tag created",
        description: `${data.name} has been created successfully.`,
      });

      onTagCreated?.();
      handleClose();
    } catch (error) {
      console.error("Error creating tag:", error);
      toast({
        title: "Error",
        description: "Failed to create tag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (watchedType === "dynamic" && !criteria) {
      toast({
        title: "Rules required",
        description: "Please define rules for automatic tagging.",
        variant: "destructive",
      });
      return;
    }
    form.handleSubmit(onSubmit)();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: form.watch("color") }}
            />
            Create Tag
          </DialogTitle>
          <DialogDescription>
            Create a tag to organize and categorize your devices
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6">
            {/* Multi-Select Customer Selection */}
            <FormField
              control={form.control}
              name="customers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customers</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Select
                        value=""
                        onValueChange={(value) => addCustomer(value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select customers" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockCustomers
                            .filter(customer => !field.value.includes(customer.id))
                            .map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Selected Customers */}
                      {field.value.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {field.value.map((customerId) => {
                            const customer = mockCustomers.find(c => c.id === customerId);
                            return customer ? (
                              <Badge
                                key={customerId}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {customer.name}
                                <button
                                  type="button"
                                  onClick={() => removeCustomer(customerId)}
                                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Name, Icon, and Color Fields */}
            <div className="grid grid-cols-[1fr,60px,60px] gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tag Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., Marketing Laptops" 
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="icon"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Icon</FormLabel>
                    <FormControl>
                      <IconSelector
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select icon"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <FormControl>
                      <Input
                        type="color"
                        {...field}
                        className="w-full h-10 border rounded cursor-pointer p-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="What is this tag for?"
                      {...field}
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tag Type Selection */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tag Type</FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Static Tag Card */}
                      <div
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-accent/50 ${
                          field.value === "static" 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => field.onChange("static")}
                      >
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-primary" />
                          <div>
                            <div className="font-medium text-sm">Static Tag</div>
                            <div className="text-xs text-muted-foreground">Manual assignment</div>
                          </div>
                        </div>
                      </div>

                      {/* Dynamic Tag Card */}
                      <div
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-accent/50 ${
                          field.value === "dynamic" 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => field.onChange("dynamic")}
                      >
                        <div className="flex items-center gap-3">
                          <Filter className="h-5 w-5 text-primary" />
                          <div className="flex-1">
                            <div className="font-medium text-sm flex items-center gap-1">
                              Dynamic Tag
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs">
                                    <p className="text-sm">
                                      Dynamic tags automatically assign themselves to devices that match your defined criteria. 
                                      Perfect for organizing devices by department, OS version, or other attributes.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="text-xs text-muted-foreground">Rule-based</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Rules - Show when dynamic */}
            {watchedType === "dynamic" && (
              <div className="space-y-4 p-4 border rounded-lg bg-accent/30">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Tagging Rules</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Define criteria to automatically assign this tag to devices that match your conditions.
                </p>
                <TagCriteriaBuilder
                  value={criteria}
                  onChange={setCriteria}
                />
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Tag"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}